import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

interface Client { id: number; name: string }

interface IssueData {
  client_id: number
  issue_type: 'brak_wody' | 'spadek_cisnienia' | 'wycieki' | 'cofka' | 'niedroznosc' | 'inne'
  reporter_name: string
  reporter_phone: string
  location: string
  street_address: string
  latitude: string
  longitude: string
  is_common_area: boolean
  additional_info: string
}

interface IssuesCreatorProps {
  token: string
  isOpen: boolean
  onClose: () => void
  onIssueCreated: () => void
  issueId?: string
}

// Trwały szkic zapisywany TYLKO po kliknięciu "Zapisz wersję roboczą"
const DRAFT_KEY = 'issue_draft_v1'

export default function IssuesCreator({
  token,
  isOpen,
  onClose,
  onIssueCreated,
  issueId
}: IssuesCreatorProps) {
  const { showError, showSuccess, showConfirm } = useNotificationContext()
  const isMobile = useIsMobile()

  const [step, setStep] = useState<number>(1)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [currentIssueId, setCurrentIssueId] = useState<string>('')
  const [uploadedImages, setUploadedImages] = useState<File[]>([])

  const emptyData: IssueData = useMemo(() => ({
    client_id: 0,
    issue_type: 'brak_wody',
    reporter_name: '',
    reporter_phone: '',
    location: '',
    street_address: '',
    latitude: '',
    longitude: '',
    is_common_area: false,
    additional_info: ''
  }), [])

  const [form, setForm] = useState<IssueData>(emptyData)
  const isEditMode = Boolean(issueId)

  const resetForm = useCallback(() => {
    setForm({ ...emptyData })
    setStep(1)
    setCurrentIssueId('')
    setUploadedImages([])
  }, [emptyData])

  const loadClients = useCallback(async () => {
    try {
      setLoadingClients(true)
      const resp = await fetch('/api/clients/list', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data: Client[] = await resp.json()
      setClients(data)
      if (!isEditMode && data.length > 0) {
        // Auto-select first client only if no client is selected
        setForm(f => f.client_id === 0 ? { ...f, client_id: data[0].id } : f)
      }
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy klientów')
    } finally {
      setLoadingClients(false)
    }
  }, [token, isEditMode, showError])

  const loadIssue = useCallback(async (id: string) => {
    try {
      const resp = await fetch(`/api/issues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error()
      const data = await resp.json()
      setCurrentIssueId(data.id)
      const merged: IssueData = {
        client_id: data.client_id ?? 0,
        issue_type: (data.issue_type ?? 'brak_wody') as IssueData['issue_type'],
        reporter_name: data.reporter_name ?? '',
        reporter_phone: data.reporter_phone ?? '',
        location: data.location ?? '',
        street_address: data.street_address ?? '',
        latitude: data.latitude ?? '',
        longitude: data.longitude ?? '',
        is_common_area: Boolean(data.is_common_area),
        additional_info: data.additional_info ?? ''
      }
      setForm(merged)
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych zgłoszenia do edycji')
      onClose()
    }
  }, [token, showError, onClose])

  useEffect(() => {
    if (isOpen) {
      loadClients()
      if (issueId) {
        loadIssue(issueId)
      } else {
        resetForm()
      }
    }
  }, [isOpen, issueId, loadClients, loadIssue, resetForm])

  const validate = (): string | null => {
    if (!form.client_id) return 'Wybierz klienta.'
    if (!form.reporter_name.trim()) return 'Wprowadź imię i nazwisko zgłaszającego.'
    if (!form.reporter_phone.trim()) return 'Wprowadź telefon kontaktowy.'
    if (!form.street_address.trim()) return 'Wprowadź adres awarii.'
    return null
  }

  // Trwały zapis szkicu — tylko na wyraźne kliknięcie
  const handleSaveDraftClick = () => {
    if (isEditMode) {
      showError('Info', 'Wersja robocza (localStorage) dostępna tylko przy tworzeniu nowego zgłoszenia.')
      return
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form))
    showSuccess('Zapisano', 'Wersja robocza zapisana lokalnie w przeglądarce.')
  }

  const clearDraft = async () => {
    if (isEditMode) return
    const ok = await showConfirm('Wyczyścić wersję roboczą?', 'Operacja usunie lokalny szkic.')
    if (!ok) return
    localStorage.removeItem(DRAFT_KEY)
    showSuccess('Wyczyszczono', 'Usunięto wersję roboczą.')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Upload obrazów dla zgłoszenia
  const uploadImagesForIssue = async (issueId: string) => {
    if (uploadedImages.length === 0) return

    try {
      const uploadPromises = uploadedImages.map(async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const resp = await fetch(`/api/issues/${issueId}/images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        })

        if (!resp.ok) {
          throw new Error(`Błąd uploadu pliku ${file.name}`)
        }

        return resp.json()
      })

      await Promise.all(uploadPromises)
      showSuccess('Sukces', `Przesłano ${uploadedImages.length} obrazów`)
    } catch (error) {
      showError('Błąd uploadu', 'Nie udało się przesłać wszystkich obrazów')
    }
  }

  // Zapis całości (kreator)
  const submitCreate = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    setSaving(true)
    try {
      const resp = await fetch('/api/issues', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zapisać zgłoszenia.'
        try { const d = await resp.json(); msg = d.detail || msg } catch {}
        showError('Błąd', msg)
        return
      }
      const data = await resp.json()

      // Upload obrazów jeśli zostały wybrane
      if (uploadedImages.length > 0) {
        await uploadImagesForIssue(data.id)
      }

      showSuccess('Sukces', `Zgłoszenie utworzone z ID: ${data.id}`)
      localStorage.removeItem(DRAFT_KEY)
      resetForm()
      onIssueCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // Zapis w edycji (formularz zbiorczy)
  const submitEdit = async () => {
    const err = validate()
    if (err) {
      showError('Błąd walidacji', err)
      return
    }
    if (!issueId) return
    setSaving(true)
    try {
      const resp = await fetch(`/api/issues/${issueId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!resp.ok) {
        let msg = 'Nie udało się zaktualizować zgłoszenia.'
        try { const d = await resp.json(); msg = d.detail || msg } catch {}
        showError('Błąd', msg)
        return
      }

      showSuccess('Zapisano', 'Zgłoszenie zaktualizowano.')
      resetForm()
      onIssueCreated()
      onClose()
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setSaving(false)
    }
  }

  // ============ UI ============

  if (!isOpen) return null

  const ClientSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Klient *</label>
      {loadingClients ? (
        <div className="text-sm text-gray-600">Ładowanie…</div>
      ) : (
        <select
          value={form.client_id}
          onChange={(e) => setForm({
            ...form,
            client_id: parseInt(e.target.value)
          })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— wybierz klienta —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  )

  const IssueTypeSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Typ zgłoszenia *</label>
      <div className="space-y-2">
        {[
          { value: 'brak_wody', label: 'Brak wody w budynku' },
          { value: 'spadek_cisnienia', label: 'Spadek ciśnienia w instalacji' },
          { value: 'wycieki', label: 'Wycieki na sieci wodociągowej (np. woda na ulicy / chodniku)' },
          { value: 'cofka', label: 'Cofka / wybicie ścieków' },
          { value: 'niedroznosc', label: 'Niedrożność kanalizacji (studzienka, przyłącze)' },
          { value: 'inne', label: 'Inne' }
        ].map(type => (
          <button
            key={type.value}
            type="button"
            onClick={() => setForm({ ...form, issue_type: type.value as IssueData['issue_type'] })}
            className={`w-full text-left px-4 py-3 rounded-md border text-sm ${
              form.issue_type === type.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>
    </div>
  )

  const ReporterSection = (
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Dane zgłaszającego</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Imię i nazwisko *</label>
          <input
            type="text"
            value={form.reporter_name}
            onChange={(e) => setForm({ ...form, reporter_name: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Jan Kowalski"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Telefon kontaktowy *</label>
          <input
            type="tel"
            value={form.reporter_phone}
            onChange={(e) => setForm({ ...form, reporter_phone: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="+48 123 456 789"
          />
        </div>
      </div>
    </div>
  )

  const LocationSection = (
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Lokalizacja awarii</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Miejscowość</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Warszawa"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Ulica/nr działki *</label>
          <input
            type="text"
            value={form.street_address}
            onChange={(e) => setForm({ ...form, street_address: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ul. Przykładowa 123"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Szerokość geograficzna</label>
          <input
            type="text"
            value={form.latitude}
            onChange={(e) => setForm({ ...form, latitude: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="52.2297"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Długość geograficzna</label>
          <input
            type="text"
            value={form.longitude}
            onChange={(e) => setForm({ ...form, longitude: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="21.0122"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Czy awaria dotyczy części wspólnych (np. sieć w drodze) / instalacji wewnętrznej (w budynku)?
        </label>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setForm({ ...form, is_common_area: false })}
            className={`px-4 py-2 rounded-md border text-sm ${
              !form.is_common_area
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Części wspólnych
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, is_common_area: true })}
            className={`px-4 py-2 rounded-md border text-sm ${
              form.is_common_area
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Instalacji wewnętrznej
          </button>
        </div>
      </div>
    </div>
  )

  const ImageUploadSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Załącz zdjęcia</label>
      <div className="space-y-4">
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            setUploadedImages(prev => [...prev, ...files])
          }}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <p className="text-xs text-gray-500">
          Maksymalnie 5 obrazów, każdy do 10MB. Obsługiwane formaty: JPG, PNG, GIF, WebP
        </p>

        {/* Podgląd wybranych obrazów */}
        {uploadedImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {uploadedImages.map((file, index) => (
              <div key={index} className="relative border border-gray-200 rounded-md overflow-hidden">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Podgląd ${index + 1}`}
                  className="w-full h-24 object-cover"
                />
                <div className="absolute top-1 right-1">
                  <button
                    onClick={() => {
                      setUploadedImages(prev => prev.filter((_, i) => i !== index))
                    }}
                    className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    title="Usuń obraz"
                  >
                    ×
                  </button>
                </div>
                <div className="p-2">
                  <div className="text-xs text-gray-600 truncate">{file.name}</div>
                  <div className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const AdditionalInfoSection = (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Dodatkowe informacje</label>
      <textarea
        rows={4}
        value={form.additional_info}
        onChange={(e) => setForm({ ...form, additional_info: e.target.value })}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Dodatkowe informacje, uwagi, szczegóły awarii..."
      />
      {ImageUploadSection}
    </div>
  )

  // ----- Tryb EDYCJI: formularz zbiorczy -----
  if (isEditMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Edycja zgłoszenia {currentIssueId}</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {ClientSection}
            {IssueTypeSection}
            {ReporterSection}
            {LocationSection}
            {AdditionalInfoSection}
          </div>

          <div className="flex justify-end gap-3 p-6 border-t">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Anuluj
            </button>
            <button
              onClick={submitEdit}
              disabled={saving || loadingClients}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ----- Tryb TWORZENIA: modal krokowy -----
  const renderStep = () => {
    switch (step) {
      case 1: return (<>{ClientSection}</>)
      case 2: return (<>{IssueTypeSection}</>)
      case 3: return (<>{ReporterSection}</>)
      case 4: return (<>{LocationSection}</>)
      case 5: return (<>{AdditionalInfoSection}</>)
      default: return null
    }
  }

  const canGoNext = (): boolean => {
    if (step === 1) return Boolean(form.client_id)
    if (step === 2) return Boolean(form.issue_type)
    if (step === 3) return form.reporter_name.trim().length > 0 && form.reporter_phone.trim().length > 0
    if (step === 4) return form.street_address.trim().length > 0
    return true
  }

  const getTotalSteps = (): number => {
    return 5
  }

  const next = () => setStep(Math.min(step + 1, getTotalSteps()))
  const prev = () => setStep(Math.max(1, step - 1))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Nowe zgłoszenie</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="text-sm text-gray-500 mb-3">Krok {step} z {getTotalSteps()}</div>
          {renderStep()}
        </div>

        {/* Footer */}
        <div className={`${isMobile ? 'flex flex-col gap-3 p-4' : 'flex items-center justify-between gap-2 p-6'} border-t`}>
          <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex items-center gap-2'}`}>
            <button
              type="button"
              onClick={handleSaveDraftClick}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md`}
            >
              Zapisz wersję roboczą
            </button>
            <button
              type="button"
              onClick={clearDraft}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md`}
            >
              Wyczyść szkic
            </button>
          </div>

          <div className={`${isMobile ? 'flex justify-between gap-2' : 'flex items-center gap-2'}`}>
            <button
              onClick={prev}
              disabled={step === 1}
              className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50`}
            >
              Wstecz
            </button>
            {step < getTotalSteps() ? (
              <button
                onClick={next}
                disabled={!canGoNext()}
                className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                Dalej
              </button>
            ) : (
              <button
                onClick={submitCreate}
                disabled={saving || loadingClients}
                className={`${isMobile ? 'flex-1 px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50`}
              >
                {saving ? 'Zapisywanie…' : 'Utwórz zgłoszenie'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
